import React, {useEffect, useState} from "react";
import {Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useMediaQuery} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import dayjs from "dayjs";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import {ComplianceCalendarRestControllerApiFactory, ComplianceCalendarRow} from "arteco-api-client-ts";
import {useApiManager} from "../../../../components/utils/api";
import {dateToString} from "../../../../components/utils/dates";
import {EmptyState, ListCard, Page, PageHeader, Panel, SkeletonRows, SoftChip, tableContainerSx, tableSx, Tone} from "../../../../components/kit";

const soon = (d?: string): { label: string, tone: Tone } => {
	if (!d) return {label: "Sin fecha", tone: "neutral"};
	const days = dayjs(d).diff(dayjs().startOf("day"), "day");
	if (days <= 14) return {label: `En ${days} ${days === 1 ? "día" : "días"}`, tone: "warning"};
	return {label: dateToString(d), tone: "neutral"};
};

/**
 * Calendario de obligaciones del sistema de gestión (#14015, man-sistema-de-gestion §9): qué toca, cada
 * cuánto, a quién y cuándo es la próxima. Catorce días antes entra en la portada de pendientes de su
 * responsable; allí se marca como hecha con la evidencia.
 */
export const ComplianceCalendarPage = () => {
	const apiManager = useApiManager();
	const ctrl = apiManager.factory(ComplianceCalendarRestControllerApiFactory);
	const theme = useTheme();
	const compact = useMediaQuery(theme.breakpoints.down("md"));
	const [rows, setRows] = useState<ComplianceCalendarRow[]>();
	const [error, setError] = useState(false);

	useEffect(() => {
		apiManager.execute(ctrl.calendarCompliance()).then(setRows).catch(() => setError(true));
	}, []);

	const sorted = [...(rows || [])].sort((a, b) => (a.nextDue || "9999").localeCompare(b.nextDue || "9999"));

	return (
		<Page>
			<PageHeader title="Calendario del sistema de gestión"
						subtitle="Las obligaciones periódicas del manual. Catorce días antes salen en la portada de su responsable, y allí se marcan como hechas con su evidencia."/>
			<Panel title="Obligaciones" subtitle="La revisión trimestral de accesos y la de cada documento avisan por su cuenta.">
				{error ? (
					<EmptyState icon={<ErrorOutlineRoundedIcon/>} title="No se ha podido cargar el calendario"
								description="Vuelve a cargar la página; si sigue fallando, avisa con el botón de abajo a la derecha."/>
				) : !rows ? <SkeletonRows rows={6}/> : rows.length === 0 ? (
					<EmptyState icon={<EventNoteOutlinedIcon/>} title="Sin obligaciones"/>
				) : compact ? (
					<Stack spacing={1.5}>
						{sorted.map(r => {
							const s = soon(r.nextDue);
							return (
								<ListCard key={r.code}>
									<Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
										<Typography variant="body2" fontWeight={600} sx={{minWidth: 0}}>{r.name}</Typography>
										<SoftChip tone={s.tone} label={s.label}/>
									</Stack>
									<Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
										{r.periodicity} · {r.responsible}
									</Typography>
									<Typography variant="caption" color="text.secondary" display="block">Se registra en: {r.record}</Typography>
								</ListCard>
							);
						})}
					</Stack>
				) : (
					<TableContainer sx={tableContainerSx}>
						<Table size="small" sx={tableSx}>
							<TableHead>
								<TableRow>
									<TableCell>Obligación</TableCell>
									<TableCell>Cada</TableCell>
									<TableCell>Responsable</TableCell>
									<TableCell>Se registra en</TableCell>
									<TableCell sx={{width: 130}}>Próxima</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{sorted.map(r => {
									const s = soon(r.nextDue);
									return (
										<TableRow key={r.code}>
											<TableCell><Typography variant="body2" fontWeight={600}>{r.name}</Typography></TableCell>
											<TableCell><Typography variant="body2">{r.periodicity}</Typography></TableCell>
											<TableCell><Typography variant="body2">{r.responsible}</Typography></TableCell>
											<TableCell><Typography variant="body2" color="text.secondary">{r.record}</Typography></TableCell>
											<TableCell>
												<Stack spacing={0.5} alignItems="flex-start">
													<Typography variant="body2" sx={{fontVariantNumeric: "tabular-nums"}}>{r.nextDue ? dateToString(r.nextDue) : "—"}</Typography>
													{s.tone === "warning" && <SoftChip tone={s.tone} label={s.label}/>}
												</Stack>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</Panel>
		</Page>
	);
};
